import { createMemo } from '@nzyme/utils/createMemo.js';
import { noop } from '@nzyme/utils/functions/noop.js';
import { resolveDeps } from './utils/resolveDeps.js';
/**
 * Creates a new service resolution strategy.
 * This is a type-safe wrapper that ensures the function matches the ServiceResolutionStrategy interface.
 *
 * @param strategy - The function that implements the resolution strategy
 * @returns The same function, properly typed as a ServiceResolutionStrategy
 * @__NO_SIDE_EFFECTS__
 */
export function defineResolutionStrategy(strategy) {
    return strategy;
}
/**
 * Gets the resolution strategy function for a given strategy type or function.
 *
 * @param strategy - The strategy type or function
 * @returns The resolution strategy function
 * @throws Error if the strategy type is invalid
 */
export function getResolutionStrategy(strategy) {
    if (typeof strategy === 'function') {
        return strategy;
    }
    switch (strategy) {
        case 'lazy':
            return lazyStrategy;
        case 'singleton':
            return singletonStrategy;
        case 'transient':
            return transientStrategy;
        default:
            throw new Error(`Invalid service resolution strategy: ${strategy}`);
    }
}
/**
 * Resolution strategy that creates and caches a single instance of the service.
 * The instance is:
 * - Created on first resolution
 * - Cached in the container
 * - Reused for subsequent resolutions
 * - Scoped to the container's scope
 */
export const singletonStrategy = defineResolutionStrategy((service, container) => {
    let instance = container.get(service);
    if (instance) {
        return instance;
    }
    while (container.scope !== service.scope) {
        if (!container.parent) {
            throw new Error(`Container with required scope ${service.scope?.name} not found for service ${service.name}`);
        }
        container = container.parent;
        instance = container.get(service);
        if (instance) {
            return instance;
        }
    }
    const deps = resolveDeps(service.deps, container, service);
    instance = service.create(deps);
    container.set(service, instance);
    if (service.implements && !container.get(service.implements)) {
        container.set(service.implements, instance);
    }
    return instance;
});
/**
 * Resolution strategy that creates a new instance of the service on each resolution.
 * The instance is:
 * - Created on each resolution
 * - Not cached in the container
 * - Scoped to the container's scope
 */
export const transientStrategy = defineResolutionStrategy((service, container, caller) => {
    if (service.scope) {
        while (container.scope !== service.scope) {
            if (!container.parent) {
                throw new Error(`Container with required scope ${service.scope?.name} not found for service ${service.name}`);
            }
            container = container.parent;
        }
    }
    const deps = resolveDeps(service.deps, container, caller);
    return service.create(deps);
});
/**
 * Resolution strategy that creates a proxy that resolves to a singleton on first use.
 * The proxy:
 * - Is created immediately
 * - Defers instance creation until first use
 * - Caches the instance after creation
 * - Forwards all operations to the cached instance
 */
export const lazyStrategy = defineResolutionStrategy((service, container, caller) => {
    // Short-circuit if already resolved
    const existing = container.get(service);
    if (existing) {
        return existing;
    }
    const memo = createMemo(() => singletonStrategy(service, container, caller));
    const proxy = new Proxy(noop, {
        get: (_target, prop) => {
            return memo()[prop];
        },
        set: (_target, prop, value) => {
            memo()[prop] = value;
            return true;
        },
        has: (_target, prop) => {
            return prop in memo();
        },
        apply: (_target, _thisArg, args) => {
            return memo()(...args);
        },
    });
    return proxy;
});
//# sourceMappingURL=serviceResolve.js.map